import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import moment from "moment";
import { Feather } from "@expo/vector-icons";
import { DatePickerModal } from "react-native-paper-dates";
import { DefaultTheme, DarkTheme, Provider as PaperProvider } from "react-native-paper";

import { useColumnResize } from "../../Reports/hooks/useColumnResize";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { useTranslation } from "../../../utils/useTranslation";
import { translations, productionTranslationMap } from "../../../utils/translations";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";
import { getWorkOrderPlanning } from "../../../services/workOrderPlanningService";
import ChinaTrackerGanttScreen from "../../ChinaTracker/ChinaTrackerGanttScreen";
import teamsConfig from "../../../utils/constants/teams";
import {
  calendarAttendanceModel,
  workCenterCalendarModel,
  workCenterPlanningModel,
  workOrderPlanningModel,
} from "../../../models/reportsModel";

const BAR_H = 24;
const BAR_GAP = 4;
const ROW_PAD = 6;
const DEFAULT_LEFT_W = 130;
const MONTH_H = 26;
const DAY_H = 24;
const TOTAL_H = 40;
const WINDOW_MONTHS = 3;
const POPOVER_W = 300;
const CARET_H = 7;

const OPEN_STATES = ["progress", "ready", "waiting", "pending"];
const ALL_STATES = [...OPEN_STATES, "done", "cancel"];

const STATE_LABELS: Record<string, keyof typeof translations> = {
  progress: "workOrderStateProgress",
  ready: "workOrderStateReady",
  waiting: "workOrderStateWaiting",
  pending: "workOrderStatePending",
  done: "workOrderStateDone",
  cancel: "workOrderStateCancel",
};

const BAR_COLORS = [
  "#E8A87C", "#C38D9E", "#85CDCB", "#E27D60", "#41B3A3", "#A2C5AC",
  "#F5B7B1", "#AEC6CF", "#D7BDE2", "#F9E79F", "#A9CCE3", "#B9E3C6",
];

const MS_PER_DAY = 86400000;

const SITE_TIME_ZONE = "Australia/Sydney";

const fieldsFormatCache = new Map<string, Intl.DateTimeFormat>();

const fieldsFormatFor = (timeZone: string) => {
  let format = fieldsFormatCache.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    fieldsFormatCache.set(timeZone, format);
  }
  return format;
};

const parseOdooDateIn = (value: string | null, timeZone: string) => {
  if (!value) return null;
  const instant = new Date(`${value.replace(" ", "T")}Z`);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = fieldsFormatFor(timeZone).formatToParts(instant);
  const field = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  return new Date(
    field("year"),
    field("month") - 1,
    field("day"),
    field("hour") % 24,
    field("minute"),
    field("second"),
  );
};

const parseOdooDate = (value: string | null) =>
  parseOdooDateIn(value, SITE_TIME_ZONE);

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toISODate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const formatDisplayDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;

const formatMobileDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}`;

const formatDateTime = (value: string | null) => {
  const date = parseOdooDate(value);
  if (!date) return "—";
  const time = date.toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${formatDisplayDate(date)}, ${time}`;
};

const parseDMY = (text: string) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!match) return null;
  const [, d, m, y] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const valid =
    date.getFullYear() === Number(y) &&
    date.getMonth() === Number(m) - 1 &&
    date.getDate() === Number(d);
  return valid ? date : null;
};

interface rangePreset {
  key: string;
  labelKey: keyof typeof translations;
  range: () => { start: Date; end: Date };
}

const RANGE_PRESETS: Array<rangePreset> = [
  {
    key: "today",
    labelKey: "today",
    range: () => ({ start: moment().startOf("day").toDate(), end: moment().startOf("day").toDate() }),
  },
  {
    key: "week",
    labelKey: "thisWeek",
    range: () => ({ start: moment().startOf("isoWeek").toDate(), end: moment().endOf("isoWeek").toDate() }),
  },
  {
    key: "month",
    labelKey: "thisMonth",
    range: () => ({ start: moment().startOf("month").toDate(), end: moment().endOf("month").toDate() }),
  },
  {
    key: "quarter",
    labelKey: "thisQuarter",
    range: () => ({ start: moment().startOf("quarter").toDate(), end: moment().endOf("quarter").toDate() }),
  },
  {
    key: "year",
    labelKey: "thisYear",
    range: () => ({ start: moment().startOf("year").toDate(), end: moment().endOf("year").toDate() }),
  },
];

const compareWorkCentres = (a: string, b: string) => {
  const left = /^(.*?)\s*(\d+)\s*$/.exec(a);
  const right = /^(.*?)\s*(\d+)\s*$/.exec(b);
  if (left && right) {
    const byPrefix = left[1].localeCompare(right[1]);
    return byPrefix !== 0 ? byPrefix : Number(left[2]) - Number(right[2]);
  }
  if (left) return -1;
  if (right) return 1;
  return a.localeCompare(b);
};

const dayDiff = (from: Date, to: Date) =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);

const formatMinutes = (minutes: number) => {
  const rounded = Math.round(minutes);
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, "0")}`;
};

const formatDurationMinutes = (minutes: number) => {
  const whole = Math.floor(minutes);
  const seconds = Math.round((minutes - whole) * 60);
  return `${whole}:${String(seconds).padStart(2, "0")}`;
};

const formatProductLabel = (order: workOrderPlanningModel) =>
  order.item_code ? `[${order.item_code}] ${order.product_name ?? ""}` : order.product_name ?? "";

const LIST_COLUMNS = [
  { key: "operation", labelKey: productionTranslationMap.operation, width: 90, align: "left" as const },
  { key: "workcenter", labelKey: productionTranslationMap.workcenter, width: 90, align: "left" as const },
  { key: "mo", labelKey: productionTranslationMap.mo, width: 150, align: "left" as const },
  { key: "customerReference", labelKey: productionTranslationMap.customerReference, width: 220, align: "left" as const },
  { key: "product", labelKey: productionTranslationMap.product, width: 330, align: "left" as const },
  { key: "qty", labelKey: productionTranslationMap.qty, width: 140, align: "right" as const },
  { key: "expected", labelKey: productionTranslationMap.expected, width: 120, align: "right" as const },
  { key: "duration", labelKey: productionTranslationMap.duration, width: 130, align: "right" as const },
  { key: "status", labelKey: productionTranslationMap.status, width: 140, align: "left" as const },
];

const DEFAULT_LIST_WIDTHS: Record<string, number> = LIST_COLUMNS.reduce(
  (acc, column) => ({ ...acc, [column.key]: column.width }),
  {},
);

const RESIZE_HANDLE = {
  position: "absolute" as const,
  right: 0,
  top: 0,
  bottom: 0,
  width: 6,
  cursor: "col-resize",
  zIndex: 2,
};

const RESIZE_HANDLE_LEFT = {
  position: "absolute" as const,
  left: 0,
  top: 0,
  bottom: 0,
  width: 6,
  cursor: "col-resize",
  zIndex: 2,
};

const colorFor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return BAR_COLORS[Math.abs(hash) % BAR_COLORS.length];
};

const getStatusColors = (state: string, isDark: boolean) => {
  switch (state) {
    case "progress":
      return isDark ? { bg: "#0C4A6E", text: "#7DD3FC" } : { bg: "#E0F2FE", text: "#0369A1" };
    case "ready":
      return isDark ? { bg: "#14532D", text: "#86EFAC" } : { bg: "#DCFCE7", text: "#166534" };
    case "waiting":
      return isDark ? { bg: "#713F12", text: "#FEF08A" } : { bg: "#FEF9C3", text: "#854D0E" };
    case "pending":
      return isDark ? { bg: "#374151", text: "#D1D5DB" } : { bg: "#F3F4F6", text: "#374151" };
    case "done":
      return isDark ? { bg: "#581C87", text: "#D8B4FE" } : { bg: "#F3E8FF", text: "#6B21A8" };
    case "cancel":
      return isDark ? { bg: "#7F1D1D", text: "#FCA5A5" } : { bg: "#FEE2E2", text: "#991B1B" };
    default:
      return isDark ? { bg: "#123", text: "#8ecfcf" } : { bg: "#e0f2f1", text: "#00695c" };
  }
};

interface positionedWorkOrder {
  order: workOrderPlanningModel;
  startIndex: number;
  endIndex: number;
  lane: number;
}

interface plannedRow {
  key: string;
  name: string;
  calendar: workCenterCalendarModel | null;
  bars: Array<positionedWorkOrder>;
  laneCount: number;
  height: number;
  top: number;
}

interface popoverAnchor {
  order: workOrderPlanningModel;
  barTop: number;
  barBottom: number;
  left: number;
  popoverLeft?: number;
}

const POPOVER_EST_H = 150;

const matchesSearch = (order: workOrderPlanningModel, terms: Array<string>) => {
  if (terms.length === 0) return true;
  const haystack = [
    order.product_name,
    order.item_code,
    order.mo_reference,
    order.name,
    order.workcenter_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.some((term) => haystack.includes(term));
};

const addWorkingMinutes = (
  totals: Array<number>,
  order: workOrderPlanningModel,
  calendar: workCenterCalendarModel | null,
  rangeStart: Date,
  totalDays: number,
) => {
  const timeZone = calendar?.tz || SITE_TIME_ZONE;
  const start = parseOdooDateIn(order.date_start, timeZone);
  const finish = parseOdooDateIn(order.date_finished, timeZone) ?? start;
  if (!start || !finish || finish <= start) return;

  const windowsByDay = new Map<number, Array<calendarAttendanceModel>>();
  (calendar?.attendances ?? []).forEach((attendance) => {
    const day = Number(attendance.dayofweek);
    if (!windowsByDay.has(day)) windowsByDay.set(day, []);
    windowsByDay.get(day)!.push(attendance);
  });

  const lastDay = startOfDay(finish);
  for (
    const cursor = startOfDay(start);
    cursor.getTime() <= lastDay.getTime();
    cursor.setDate(cursor.getDate() + 1)
  ) {
    const index = dayDiff(rangeStart, cursor);
    if (index < 0 || index >= totalDays) continue;

    const windows = windowsByDay.get((cursor.getDay() + 6) % 7);
    if (!windows) continue;

    windows.forEach((window) => {
      const windowStart = new Date(cursor);
      windowStart.setHours(0, 0, 0, 0);
      windowStart.setMinutes(Math.round(window.hour_from * 60));
      const windowEnd = new Date(cursor);
      windowEnd.setHours(0, 0, 0, 0);
      windowEnd.setMinutes(Math.round(window.hour_to * 60));

      const overlap =
        Math.min(finish.getTime(), windowEnd.getTime()) -
        Math.max(start.getTime(), windowStart.getTime());
      if (overlap > 0) totals[index] += overlap / 60000;
    });
  }
};

const assignLanes = (orders: Array<positionedWorkOrder>) => {
  const laneEnds: Array<number> = [];
  orders
    .slice()
    .sort((a, b) => a.startIndex - b.startIndex)
    .forEach((item) => {
      let lane = laneEnds.findIndex((end) => end <= item.startIndex);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = item.endIndex + 1;
      item.lane = lane;
    });
  return laneEnds.length;
};

export default function WorkOrderPlanningScreen() {
  const { colors, isDark } = useAppTheme();
  const { t } = useTranslation();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const [availableWidth, setAvailableWidth] = useState<number>(0);

  const [rangeStart, setRangeStart] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [rangeEnd, setRangeEnd] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + WINDOW_MONTHS, 0);
  });
  const [activePresetKey, setActivePresetKey] = useState<string>("custom");
  const [isRangePickerOpen, setIsRangePickerOpen] = useState<boolean>(false);
  const [fromText, setFromText] = useState<string>("");
  const [toText, setToText] = useState<string>("");
  const [isFromCalendarOpen, setIsFromCalendarOpen] = useState<boolean>(false);
  const [isToCalendarOpen, setIsToCalendarOpen] = useState<boolean>(false);
  const [groups, setGroups] = useState<Array<workCenterPlanningModel>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedStates, setSelectedStates] = useState<Array<string>>(OPEN_STATES);
  const [selected, setSelected] = useState<popoverAnchor | null>(null);
  const [popoverHeight, setPopoverHeight] = useState<number>(POPOVER_EST_H);
  const [searchCriteria, setSearchCriteria] = useState<Array<string>>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const searchScrollRef = useRef<any>(null);
  const popoverRef = useRef<any>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || !selected) return;

    const handleClick = (e: any) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) {
        return;
      }
      setSelected(null);
    };

    window.addEventListener("mousedown", handleClick, true);
    window.addEventListener("touchstart", handleClick, true);

    return () => {
      window.removeEventListener("mousedown", handleClick, true);
      window.removeEventListener("touchstart", handleClick, true);
    };
  }, [selected]);

  const onSearchChange = (text: string) => {
    if (text.includes(",")) {
      const before = text.slice(0, text.indexOf(",")).trim();
      if (before) setSearchCriteria((current) => [...current, before]);
      setSearchInput("");
    } else {
      setSearchInput(text);
    }
  };

  const commitSearchInput = () => {
    const trimmed = searchInput.trim();
    if (trimmed) {
      setSearchCriteria((current) => [...current, trimmed]);
      setSearchInput("");
    }
  };
  const { userInfo } = useSelector((state: any) => state.configurations);
  // Sales managers only see QLD planning
  const canViewChinaPlanning =
    userInfo?.team?.toUpperCase() !== teamsConfig.teams.salesManager;

  const [viewMode, setViewMode] = useState<"gantt" | "list" | "china">("gantt");

  useEffect(() => {
    AsyncStorage.getItem("@WorkOrderPlanning_viewMode").then((stored) => {
      if (stored === "list" || stored === "gantt" || (stored === "china" && canViewChinaPlanning)) {
        setViewMode(stored);
      }
    });
  }, [canViewChinaPlanning]);

  const handleSetViewMode = (mode: "gantt" | "list" | "china") => {
    setViewMode(mode);
    AsyncStorage.setItem("@WorkOrderPlanning_viewMode", mode);
  };

  const [expandedKeys, setExpandedKeys] = useState<Array<string>>([]);
  const [listWidths, setListWidths] = useState<Record<string, number>>(DEFAULT_LIST_WIDTHS);
  const startResize = useColumnResize(setListWidths);
  const [listHeaderH, setListHeaderH] = useState<number>(34);
  const [listFooterH, setListFooterH] = useState<number>(34);
  const [listRowsTop, setListRowsTop] = useState<number>(0);
  const listRowsRef = useRef<any>(null);

  useEffect(() => {
    if (availableWidth > 0) {
      setListWidths((prev) => {
        const baseListWidth = LIST_COLUMNS.reduce(
          (sum, column) => sum + DEFAULT_LIST_WIDTHS[column.key],
          0
        );
        const extra = availableWidth - 24 - baseListWidth;

        if (extra > 0) {
          return { ...DEFAULT_LIST_WIDTHS, product: DEFAULT_LIST_WIDTHS["product"] + extra };
        } else if (extra < 0) {
          const targetTotalWidth = Math.max(800, availableWidth - 24);
          const ratio = targetTotalWidth / baseListWidth;

          const newWidths: Record<string, number> = {};
          LIST_COLUMNS.forEach(col => {
            newWidths[col.key] = Math.max(60, Math.floor(DEFAULT_LIST_WIDTHS[col.key] * ratio));
          });
          return newWidths;
        }
        return { ...DEFAULT_LIST_WIDTHS };
      });
    }
  }, [availableWidth, refreshKey]);

  const columnWidth = (key: string) => listWidths[key] ?? DEFAULT_LIST_WIDTHS[key];
  const listWidth = LIST_COLUMNS.reduce((sum, column) => sum + columnWidth(column.key), 0);

  const searchTerms = useMemo(
    () =>
      [...searchCriteria, searchInput.trim()]
        .map((term) => term.toLowerCase())
        .filter(Boolean),
    [searchCriteria, searchInput],
  );

  const totalDays = useMemo(() => dayDiff(rangeStart, rangeEnd) + 1, [rangeStart, rangeEnd]);

  const dayWidth = 44;

  const styles = useMemo(() => createStyles(colors, isDark, dayWidth), [colors, isDark, dayWidth]);

  const gridWidth = totalDays * dayWidth;

  const scrollAnim = useRef(new Animated.Value(0)).current;
  const negScrollAnim = useRef(Animated.multiply(scrollAnim, -1)).current;
  const onBodyScroll = useRef(
    Animated.event([{ nativeEvent: { contentOffset: { x: scrollAnim } } }], {
      useNativeDriver: false,
    }),
  ).current;

  const scrollXRef = useRef(0);
  useEffect(() => {
    const id = scrollAnim.addListener(({ value }) => {
      scrollXRef.current = value;
    });
    return () => scrollAnim.removeListener(id);
  }, [scrollAnim]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setSelected(null);
    const request =
      viewMode === "list"
        ? getWorkOrderPlanning()
        : getWorkOrderPlanning(toISODate(rangeStart), toISODate(rangeEnd));
    request.then((data) => {
      if (cancelled) return;
      setGroups(data);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd, viewMode, refreshKey]);

  const days = useMemo(
    () =>
      Array.from({ length: totalDays }, (_, index) => {
        const date = new Date(
          rangeStart.getFullYear(),
          rangeStart.getMonth(),
          rangeStart.getDate() + index,
        );
        const weekday = date.getDay();
        return { date, isWeekend: weekday === 0 || weekday === 6 };
      }),
    [rangeStart, totalDays],
  );

  const months = useMemo(() => {
    const result: Array<{ label: string; span: number; startX: number }> = [];
    let currentX = 0;
    days.forEach(({ date }) => {
      const label = date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
      const last = result[result.length - 1];
      if (last && last.label === label) last.span += 1;
      else result.push({ label, span: 1, startX: currentX });
      currentX += dayWidth;
    });
    return result;
  }, [days, dayWidth]);

  const todayIndex = useMemo(() => {
    const index = dayDiff(rangeStart, new Date());
    return index >= 0 && index < totalDays ? index : -1;
  }, [rangeStart, totalDays]);

  const rows: Array<plannedRow> = useMemo(() => {
    let offset = 0;
    return groups
      .slice()
      .sort((a, b) =>
        compareWorkCentres(a.workcenter_name ?? "", b.workcenter_name ?? ""),
      )
      .map((group) => {
        const bars: Array<positionedWorkOrder> = [];

        group.work_orders.forEach((order) => {
          if (!selectedStates.includes(order.state)) return;
          if (!matchesSearch(order, searchTerms)) return;
          const start = parseOdooDate(order.date_start);
          const finish = parseOdooDate(order.date_finished) ?? start;
          if (!start || !finish) return;

          const rawStart = dayDiff(rangeStart, start);
          const rawEnd = dayDiff(rangeStart, finish);
          const startIndex = Math.max(0, Math.min(rawStart, totalDays - 1));
          const endIndex = Math.max(startIndex, Math.min(rawEnd, totalDays - 1));

          bars.push({ order, startIndex, endIndex, lane: 0 });
        });

        const laneCount = assignLanes(bars);
        const height = Math.max(1, laneCount) * (BAR_H + BAR_GAP) + ROW_PAD * 2;
        const top = offset;
        offset += height;

        return {
          key: String(group.workcenter_id ?? "unassigned"),
          name: group.workcenter_name ?? t("unassigned"),
          calendar: group.calendar ?? null,
          bars,
          laneCount,
          height,
          top,
        };
      });
  }, [groups, selectedStates, searchTerms, rangeStart, totalDays, t]);

  const dailyMinutes = useMemo(() => {
    const totals = new Array(totalDays).fill(0);
    rows.forEach((row) =>
      row.bars.forEach(({ order }) =>
        addWorkingMinutes(totals, order, row.calendar, rangeStart, totalDays),
      ),
    );
    return totals;
  }, [rows, rangeStart, totalDays]);

  const listGroups = useMemo(
    () =>
      groups
        .slice()
        .sort((a, b) =>
          compareWorkCentres(a.workcenter_name ?? "", b.workcenter_name ?? ""),
        )
        .map((group) => {
          const orders = group.work_orders.filter(
            (order) =>
              selectedStates.includes(order.state) &&
              matchesSearch(order, searchTerms),
          );
          return {
            key: String(group.workcenter_id ?? "unassigned"),
            name: group.workcenter_name ?? t("unassigned"),
            orders,
            expectedMinutes: orders.reduce((sum, o) => sum + (o.duration_expected ?? 0), 0),
            durationMinutes: orders.reduce((sum, o) => sum + (o.duration ?? 0), 0),
          };
        })
        .filter((group) => group.orders.length > 0),
    [groups, selectedStates, searchTerms, t],
  );

  const listTotals = useMemo(
    () => ({
      expectedMinutes: listGroups.reduce((sum, g) => sum + g.expectedMinutes, 0),
      durationMinutes: listGroups.reduce((sum, g) => sum + g.durationMinutes, 0),
      count: listGroups.reduce((sum, g) => sum + g.orders.length, 0),
    }),
    [listGroups],
  );

  const toggleExpanded = (key: string) =>
    setExpandedKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );

  type mobileListItem =
    | { type: "header"; group: (typeof listGroups)[number] }
    | { type: "card"; group: (typeof listGroups)[number]; order: workOrderPlanningModel };

  const mobileFlatItems = useMemo<Array<mobileListItem>>(() => {
    const items: Array<mobileListItem> = [];
    listGroups.forEach((group) => {
      items.push({ type: "header", group });
      if (expandedKeys.includes(group.key)) {
        group.orders.forEach((order) => items.push({ type: "card", group, order }));
      }
    });
    return items;
  }, [listGroups, expandedKeys]);

  const mobileStickyIndices = useMemo(
    () =>
      mobileFlatItems.reduce<Array<number>>(
        (acc, item, index) => (item.type === "header" ? [...acc, index] : acc),
        [],
      ),
    [mobileFlatItems],
  );

  const visibleCount = useMemo(
    () => rows.reduce((sum, row) => sum + row.bars.length, 0),
    [rows],
  );

  const exportToExcel = () => {
    const source: Array<workOrderPlanningModel> =
      viewMode === "list"
        ? listGroups.flatMap((group) => group.orders)
        : rows.flatMap((row) => row.bars.map((bar) => bar.order));

    const data = source.map((order) => ({
      [t("workCenter")]: order.workcenter_name ?? "",
      [t("manufacturingOrder")]: order.mo_reference ?? "",
      [t("customerReference")]: order.customer_reference ?? t("unknown"),
      [t("product")]: formatProductLabel(order),
      [t("operation")]: order.name,
      [t("startDate")]: formatDateTime(order.date_start),
      [t("stopDate")]: formatDateTime(order.date_finished),
      [t("expectedDuration")]: formatDurationMinutes(order.duration_expected ?? 0),
      [t("durationMinutes")]: formatDurationMinutes(order.duration ?? 0),
      [t("quantityRequired")]: order.qty_production ?? 0,
      [t("status")]: t(STATE_LABELS[order.state]),
    }));

    const windowTag =
      viewMode === "gantt" ? `_${toISODate(rangeStart)}_to_${toISODate(rangeEnd)}` : "";
    exportDataToExcel(
      data,
      `Work_Orders_Planning${windowTag}_${new Date().toISOString().slice(0, 10)}`,
      "Work Orders Planning",
    );
  };

  const contentHeight = useMemo(
    () => rows.reduce((sum, row) => sum + row.height, 0),
    [rows],
  );

  const toggleState = (state: string) =>
    setSelectedStates((current) =>
      current.includes(state)
        ? current.filter((item) => item !== state)
        : [...current, state],
    );

  const openRangePicker = () => {
    setFromText(formatDisplayDate(rangeStart));
    setToText(formatDisplayDate(rangeEnd));
    setIsRangePickerOpen(true);
  };

  const applyPreset = (preset: rangePreset) => {
    const { start, end } = preset.range();
    setRangeStart(start);
    setRangeEnd(end);
    setActivePresetKey(preset.key);
    setIsRangePickerOpen(false);
  };

  const shiftWindow = (direction: 1 | -1) => {
    setRangeStart((current) => moment(current).add(3 * direction, "months").toDate());
    setRangeEnd((current) => moment(current).add(3 * direction, "months").toDate());
    setActivePresetKey("custom");
  };

  const applyCustomRange = () => {
    const from = parseDMY(fromText);
    const to = parseDMY(toText);
    if (!from || !to) return;
    setRangeStart(from <= to ? from : to);
    setRangeEnd(from <= to ? to : from);
    setActivePresetKey("custom");
    setIsRangePickerOpen(false);
  };

  const onConfirmFromDate = (params: any) => {
    setIsFromCalendarOpen(false);
    if (params.date) {
      setFromText(moment(params.date).format("DD/MM/YYYY"));
    }
  };

  const onConfirmToDate = (params: any) => {
    setIsToCalendarOpen(false);
    if (params.date) {
      setToText(moment(params.date).format("DD/MM/YYYY"));
    }
  };

  const refreshData = () => {
    setSearchCriteria([]);
    setSearchInput("");
    setSelectedStates(OPEN_STATES);
    setListWidths(DEFAULT_LIST_WIDTHS);
    const now = new Date();
    setRangeStart(new Date(now.getFullYear(), now.getMonth(), 1));
    setRangeEnd(new Date(now.getFullYear(), now.getMonth() + WINDOW_MONTHS, 0));
    setRefreshKey((k) => k + 1);
  };

  const bodyHeight = Math.min(Math.max(200, windowHeight - 340), contentHeight);

  const listRowsMaxHeight = Math.max(80, windowHeight - 340 - listHeaderH - listFooterH);
  const desktopListRowsMaxHeight =
    listRowsTop > 0 ? Math.max(80, windowHeight - listRowsTop - listFooterH - 24 - 2) : listRowsMaxHeight;
  const leftW = isMobile ? 85 : DEFAULT_LEFT_W;

  const chipContent = ALL_STATES.map((state) => {
    const active = selectedStates.includes(state);
    return (
      <Pressable
        key={state}
        onPress={() => toggleState(state)}
        style={[styles.chip, active && styles.chipActive]}
      >
        <Text style={[styles.chipText, active && styles.chipTextActive]}>
          {t(STATE_LABELS[state])}
        </Text>
      </Pressable>
    );
  });

  const filterChips = isMobile ? (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ marginTop: 8, width: "100%" }}
      contentContainerStyle={{ flexDirection: "row", gap: 6, paddingHorizontal: 4 }}
    >
      {chipContent}
    </ScrollView>
  ) : (
    <View style={styles.chipGroup}>
      {chipContent}
    </View>
  );

  const viewToggleElement = (
    <View style={styles.viewToggle}>
      {(["gantt", "list"] as const).map((mode) => (
        <Pressable
          key={mode}
          onPress={() => handleSetViewMode(mode)}
          style={[styles.viewButton, viewMode === mode && styles.viewButtonActive]}
        >
          <Text
            style={[styles.viewButtonText, viewMode === mode && styles.viewButtonTextActive]}
          >
            {t(mode === "gantt" ? "ganttView" : "listView")}
          </Text>
        </Pressable>
      ))}
    </View>
  );

  const screenSwitchElement = canViewChinaPlanning ? (
    <View style={[styles.viewToggle, { alignSelf: "center", marginLeft: "auto", padding: 2, borderRadius: 6 }]}>
      {(["qld", "china"] as const).map((screen) => {
        const active = screen === "china" ? viewMode === "china" : viewMode !== "china";
        return (
          <Pressable
            key={screen}
            onPress={() => !active && handleSetViewMode(screen === "china" ? "china" : "gantt")}
            style={[styles.viewButton, { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 4 }, active && styles.viewButtonActive]}
          >
            <Text style={[styles.viewButtonText, { fontSize: 11 }, active && styles.viewButtonTextActive]}>
              {t(screen === "china" ? "chinaPlanning" : "qldPlanning")}
            </Text>
          </Pressable>
        );
      })}
    </View>
  ) : null;

  return (
    <View
      style={styles.container}
      onLayout={(e) => setAvailableWidth(e.nativeEvent.layout.width)}
    >
      <View style={[styles.toolbar, viewMode === "china" && styles.hidden]}>
        <View style={{ width: "100%", flexDirection: "row", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <Text style={styles.title}>{t("qldWorkOrderPlanning")}</Text>
          <Text style={{ fontSize: 10, fontStyle: "italic", color: isDark ? colors.textSecondary : "#64748B" }}>
            {t("integratedFromOdoo")}
          </Text>
          {screenSwitchElement}
        </View>

        <View style={isMobile ? { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 4, width: "100%", marginBottom: viewMode === "china" ? 0 : 12, zIndex: 10 } : { zIndex: 10 }}>
          {isMobile && (
            <View style={{ marginRight: viewMode === "list" || viewMode === "china" ? "auto" : 0 }}>
              {viewToggleElement}
            </View>
          )}

          <View style={[styles.rangeGroup, isMobile && { marginBottom: 0, gap: 4, flexShrink: 1 }, (viewMode === "list" || viewMode === "china") && styles.hidden]}>
            <Pressable style={[styles.navButton, isMobile && { paddingHorizontal: 8 }]} onPress={() => shiftWindow(-1)}>
              <Text style={styles.navButtonText}>‹</Text>
            </Pressable>

            <View style={styles.rangeFieldWrapper}>
              <Pressable style={[styles.rangeField, isMobile && { paddingHorizontal: 8 }]} onPress={openRangePicker}>
                <Text style={[styles.rangeFieldText, isMobile && { fontSize: 12 }]}>
                  📅 {isMobile ? formatMobileDate(rangeStart) : formatDisplayDate(rangeStart)} → {isMobile ? formatMobileDate(rangeEnd) : formatDisplayDate(rangeEnd)}
                </Text>
              </Pressable>

              {isRangePickerOpen && (
                <>
                  <Pressable
                    style={styles.rangeBackdrop}
                    onPress={() => setIsRangePickerOpen(false)}
                  />
                  <View style={[styles.rangeDropdown, isMobile ? { right: 0 } : { left: 0, minWidth: 260 }]}>
                    {RANGE_PRESETS.map((preset) => {
                      const isActive = activePresetKey === preset.key;
                      return (
                        <Pressable
                          key={preset.key}
                          style={[styles.presetRow, { flexDirection: "row", alignItems: "center", gap: 8 }]}
                          onPress={() => applyPreset(preset)}
                        >
                          <Text style={[styles.customCheck, !isActive && { color: "transparent" }, { width: 14 }]}>✓</Text>
                          <Text style={[styles.presetText, isActive && { fontWeight: "700", color: "#1976d2" }]}>
                            {t(preset.labelKey)}
                          </Text>
                        </Pressable>
                      );
                    })}

                    <View style={styles.rangeDivider} />

                    <View style={styles.customContainer}>
                      <View style={styles.customDatesRow}>
                        <Text style={[styles.customCheck, activePresetKey !== "custom" && { color: "transparent" }, { width: 14 }]}>✓</Text>
                        <Text style={styles.customLabel}>{t("from")}</Text>

                        <View style={[styles.inputWrapper, !isMobile && { width: 120 }]}>
                          <TextInput
                            style={styles.customInputIconized}
                            value={fromText}
                            onChangeText={setFromText}
                            placeholder="DD/MM/YYYY"
                            placeholderTextColor={isDark ? "#777" : "#bbb"}
                          />
                          <Pressable
                            style={styles.calendarIconBtn}
                            onPress={() => setIsFromCalendarOpen(true)}
                          >
                            <Feather name="calendar" size={14} color="#1976d2" />
                          </Pressable>
                        </View>

                        <Text style={styles.customLabel}>{t("to")}</Text>

                        <View style={[styles.inputWrapper, !isMobile && { width: 120 }]}>
                          <TextInput
                            style={styles.customInputIconized}
                            value={toText}
                            onChangeText={setToText}
                            placeholder="DD/MM/YYYY"
                            placeholderTextColor={isDark ? "#777" : "#bbb"}
                          />
                          <Pressable
                            style={styles.calendarIconBtn}
                            onPress={() => setIsToCalendarOpen(true)}
                          >
                            <Feather name="calendar" size={14} color="#1976d2" />
                          </Pressable>
                        </View>
                      </View>

                      <Pressable style={styles.applyButton} onPress={applyCustomRange}>
                        <Text style={styles.applyButtonText}>{t("apply")}</Text>
                      </Pressable>
                    </View>
                  </View>
                </>
              )}
            </View>

            <Pressable style={[styles.navButton, isMobile && { paddingHorizontal: 8 }]} onPress={() => shiftWindow(1)}>
              <Text style={styles.navButtonText}>›</Text>
            </Pressable>
          </View>
        </View>

        {!isMobile && viewMode !== "china" && filterChips}

        {!isMobile && <View style={{ flex: 1, minWidth: 16 }} />}

        {!isMobile && (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
            {viewMode !== "china" && (
              <Text style={styles.countText}>
                {viewMode === "list" ? listTotals.count : visibleCount} {t("workOrders")}
              </Text>
            )}

            {viewToggleElement}

            {viewMode !== "china" && (
              <Pressable style={styles.refreshButton} onPress={refreshData}>
                <Feather name="refresh-cw" size={14} color={isDark ? "#aaa" : "#6B7280"} />
              </Pressable>
            )}
          </View>
        )}

        <View style={[isMobile ? { width: "100%", flexDirection: "row", alignItems: "center", marginTop: 4 } : { flexDirection: "row", alignItems: "center", gap: 12 }, viewMode === "china" && styles.hidden]}>
          {isMobile && (
            <Pressable style={[styles.refreshButton, { marginRight: 8 }]} onPress={refreshData}>
              <Feather name="refresh-cw" size={14} color={isDark ? "#aaa" : "#6B7280"} />
            </Pressable>
          )}

          {isMobile && (
            <Pressable
              style={[
                styles.downloadButton,
                { marginRight: 8, backgroundColor: showMobileFilters ? colors.primary : (isDark ? "#1E293B" : "#F8FAFC"), borderColor: showMobileFilters ? colors.primary : (isDark ? "#334155" : "#E2E8F0") }
              ]}
              onPress={() => setShowMobileFilters(!showMobileFilters)}
            >
              <Feather
                name="filter"
                size={14}
                color={showMobileFilters ? "#FFFFFF" : "#6B7280"}
              />
            </Pressable>
          )}

          <View style={[styles.searchContainer, isMobile && { flex: 1, width: "auto" }]}>
            <ScrollView
              ref={searchScrollRef}
              onContentSizeChange={() =>
                searchScrollRef.current?.scrollToEnd({ animated: true })
              }
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ flex: 1 }}
              contentContainerStyle={{ alignItems: "center", flexGrow: 1 }}
            >
              {searchCriteria.map((term, index) => (
                <View key={`${term}-${index}`} style={styles.searchChip}>
                  <Text style={styles.searchChipText}>{term}</Text>
                  <Pressable
                    style={styles.searchChipRemove}
                    onPress={() =>
                      setSearchCriteria((current) => current.filter((_, i) => i !== index))
                    }
                  >
                    <Text style={styles.searchChipX}>✕</Text>
                  </Pressable>
                </View>
              ))}
              <TextInput
                style={styles.searchInputInner}
                value={searchInput}
                onChangeText={onSearchChange}
                placeholder={searchCriteria.length === 0 ? t("searchBar") : t("addCriteria")}
                placeholderTextColor="#aaa"
                onSubmitEditing={commitSearchInput}
                blurOnSubmit={false}
              />
            </ScrollView>
          </View>

          {!isMobile && (
            <Pressable style={styles.downloadButton} onPress={exportToExcel}>
              <Feather name="download" size={14} color={isDark ? "#aaa" : "#6B7280"} />
            </Pressable>
          )}
        </View>

        {isMobile && showMobileFilters && filterChips}
      </View>

      {viewMode === "china" ? (
        <ChinaTrackerGanttScreen headerExtra={screenSwitchElement} />
      ) : isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1976d2" />
          <Text style={styles.loadingText}>{t("loading")}</Text>
        </View>
      ) : viewMode === "list" ? (
        listGroups.length === 0 ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>{t("noData")}</Text>
          </View>
        ) : isMobile ? (
          <ScrollView
            style={{ maxHeight: listRowsMaxHeight }}
            nestedScrollEnabled
            stickyHeaderIndices={mobileStickyIndices}
          >
            {mobileFlatItems.map((item, index) => {
              if (item.type === "header") {
                const isExpanded = expandedKeys.includes(item.group.key);
                return (
                  <Pressable
                    key={`header-${item.group.key}`}
                    style={[styles.mobileGroupHeader, index > 0 && { marginTop: 12 }]}
                    onPress={() => toggleExpanded(item.group.key)}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.mobileGroupTitle} numberOfLines={1}>
                        {isExpanded ? "▼" : "▶"}  {item.group.name} ({item.group.orders.length})
                      </Text>
                    </View>
                    <View style={styles.mobileGroupTotals}>
                      <Text style={styles.mobileGroupTotalText}>
                        {t("expectedDuration")}: {formatDurationMinutes(item.group.expectedMinutes)}
                      </Text>
                      <Text style={styles.mobileGroupTotalText}>
                        {t("durationMinutes")}: {formatDurationMinutes(item.group.durationMinutes)}
                      </Text>
                    </View>
                  </Pressable>
                );
              }

              const { order } = item;
              const statusColors = getStatusColors(order.state, isDark);
              return (
                <View key={order.id} style={styles.mobileCard}>
                  <View style={styles.mobileCardHeader}>
                    <View style={{ flex: 1, paddingRight: 8 }}>
                      <Text style={styles.mobileCardTitle} numberOfLines={1}>
                        {order.name}
                      </Text>
                      <Text style={styles.mobileCardSubtitle} numberOfLines={1}>
                        {order.mo_reference} · {order.workcenter_name}
                      </Text>
                    </View>
                    <View
                      style={[styles.statusChip, { marginHorizontal: 0, backgroundColor: statusColors.bg }]}
                    >
                      <Text
                        style={[styles.statusChipText, { color: statusColors.text }]}
                        numberOfLines={1}
                      >
                        {t(STATE_LABELS[order.state])}
                      </Text>
                    </View>
                  </View>

                  <Text style={styles.mobileCardProduct} numberOfLines={2}>
                    {formatProductLabel(order)}
                  </Text>

                  <View style={styles.mobileCardFooter}>
                    <View style={styles.mobileMetaChip}>
                      <Feather name="hash" size={11} color={isDark ? colors.textSecondary : "#64748B"} />
                      <Text style={styles.mobileMetaChipText} numberOfLines={1}>
                        {order.customer_reference ?? t("unknown")}
                      </Text>
                    </View>
                    <View style={styles.mobileMetaChip}>
                      <Feather name="package" size={11} color={isDark ? colors.textSecondary : "#64748B"} />
                      <Text style={styles.mobileMetaChipText}>
                        {(order.qty_production ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                    </View>
                    <View style={styles.mobileMetaChip}>
                      <Feather name="clock" size={11} color={isDark ? colors.textSecondary : "#64748B"} />
                      <Text style={styles.mobileMetaChipText}>
                        {formatDurationMinutes(order.duration_expected ?? 0)} / {formatDurationMinutes(order.duration ?? 0)}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            })}

            <View style={[styles.mobileTotalRow, { marginTop: 12 }]}>
              <Text style={styles.mobileTotalText}>
                {t("expectedDuration")}: {formatDurationMinutes(listTotals.expectedMinutes)}
              </Text>
              <Text style={styles.mobileTotalText}>
                {t("durationMinutes")}: {formatDurationMinutes(listTotals.durationMinutes)}
              </Text>
            </View>
          </ScrollView>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator
            style={{
              backgroundColor: colors.surface,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: isDark ? "#333" : "#E2E8F0",
              overflow: "hidden",
            }}
            contentContainerStyle={{ minWidth: "100%" }}
          >
            <View>
              <View
                style={styles.listHeaderRow}
                onLayout={(e) => setListHeaderH(e.nativeEvent.layout.height)}
              >
                {LIST_COLUMNS.map((column, index) => (
                  <View key={column.key} style={{ width: columnWidth(column.key) }}>
                    <Text
                      style={[styles.listHeaderText, { textAlign: column.align }]}
                      numberOfLines={1}
                    >
                      {t(column.labelKey)}
                    </Text>
                    {Platform.OS === "web" && (
                      <View
                        style={RESIZE_HANDLE}
                        {...({
                          onMouseDown: (event: any) =>
                            startResize(event, column.key, columnWidth(column.key)),
                        } as any)}
                      />
                    )}
                    {Platform.OS === "web" && index > 0 && (
                      <View
                        style={RESIZE_HANDLE_LEFT}
                        {...({
                          onMouseDown: (event: any) =>
                            startResize(
                              event,
                              LIST_COLUMNS[index - 1].key,
                              columnWidth(LIST_COLUMNS[index - 1].key)
                            ),
                        } as any)}
                      />
                    )}
                  </View>
                ))}
              </View>

              <ScrollView
                ref={listRowsRef}
                style={{ maxHeight: desktopListRowsMaxHeight }}
                onLayout={() =>
                  listRowsRef.current?.measureInWindow?.((_x: number, y: number) => setListRowsTop(y))
                }
                nestedScrollEnabled
              >
                {listGroups.map((group) => {
                  const isExpanded = expandedKeys.includes(group.key);
                  return (
                    <View key={group.key}>
                      <Pressable
                        style={styles.listGroupRow}
                        onPress={() => toggleExpanded(group.key)}
                      >
                        <Text
                          style={[
                            styles.listGroupLabel,
                            {
                              width: LIST_COLUMNS.slice(0, 6).reduce(
                                (sum, column) => sum + columnWidth(column.key),
                                0,
                              ),
                            }
                          ]}
                        >
                          {isExpanded ? "▼" : "▶"}  {group.name} ({group.orders.length})
                        </Text>
                        <Text style={[styles.listGroupCell, { width: columnWidth("expected") }]}>
                          {formatDurationMinutes(group.expectedMinutes)}
                        </Text>
                        <Text style={[styles.listGroupCell, { width: columnWidth("duration") }]}>
                          {formatDurationMinutes(group.durationMinutes)}
                        </Text>
                        <View style={{ width: columnWidth("status") }} />
                      </Pressable>

                      {isExpanded &&
                        group.orders.map((order, index) => (
                          <View
                            key={order.id}
                            style={[styles.listRow, index % 2 === 1 && styles.listRowAlt]}
                          >
                            <Text style={[styles.listCell, { width: columnWidth("operation") }]} numberOfLines={1}>
                              {order.name}
                            </Text>
                            <Text style={[styles.listCell, { width: columnWidth("workcenter") }]} numberOfLines={1}>
                              {order.workcenter_name}
                            </Text>
                            <Text style={[styles.listCell, { width: columnWidth("mo") }]} numberOfLines={1}>
                              {order.mo_reference}
                            </Text>
                            <Text
                              style={[styles.listCell, { width: columnWidth("customerReference") }]}
                              numberOfLines={1}
                            >
                              {order.customer_reference ?? t("unknown")}
                            </Text>
                            <Text style={[styles.listCell, { width: columnWidth("product") }]} numberOfLines={1}>
                              {formatProductLabel(order)}
                            </Text>
                            <Text style={[styles.listCell, styles.listCellRight, { width: columnWidth("qty") }]}>
                              {(order.qty_production ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Text>
                            <Text style={[styles.listCell, styles.listCellRight, { width: columnWidth("expected") }]}>
                              {formatDurationMinutes(order.duration_expected ?? 0)}
                            </Text>
                            <Text style={[styles.listCell, styles.listCellRight, { width: columnWidth("duration") }]}>
                              {formatDurationMinutes(order.duration ?? 0)}
                            </Text>
                            <View style={{ width: columnWidth("status"), justifyContent: "center" }}>
                              <View style={[styles.statusChip, { backgroundColor: getStatusColors(order.state, isDark).bg }]}>
                                <Text style={[styles.statusChipText, { color: getStatusColors(order.state, isDark).text }]} numberOfLines={1}>
                                  {t(STATE_LABELS[order.state])}
                                </Text>
                              </View>
                            </View>
                          </View>
                        ))}
                    </View>
                  );
                })}
              </ScrollView>

              <View
                style={styles.listTotalRow}
                onLayout={(e) => setListFooterH(e.nativeEvent.layout.height)}
              >
                <View
                  style={{
                    width: LIST_COLUMNS.slice(0, 6).reduce(
                      (sum, column) => sum + columnWidth(column.key),
                      0,
                    ),
                  }}
                />
                <Text style={[styles.listTotalCell, { width: columnWidth("expected") }]}>
                  {formatDurationMinutes(listTotals.expectedMinutes)}
                </Text>
                <Text style={[styles.listTotalCell, { width: columnWidth("duration") }]}>
                  {formatDurationMinutes(listTotals.durationMinutes)}
                </Text>
                <View style={{ width: columnWidth("status") }} />
              </View>
            </View>
          </ScrollView>
        )
      ) : rows.length === 0 ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t("noData")}</Text>
        </View>
      ) : (
        <>
          <View style={styles.headerRow}>
            <View style={[styles.cornerCell, { width: leftW }]}>
              <Text style={styles.cornerText} numberOfLines={1}>{t("operations")}</Text>
            </View>
            <View style={styles.headerClip}>
              <Animated.View
                style={{ width: gridWidth, transform: [{ translateX: negScrollAnim }] }}
              >
                <View style={styles.monthRow}>
                  {months.map((month, index) => {
                    const monthWidth = month.span * dayWidth;
                    const maxTranslate = Math.max(0, monthWidth - 110);
                    const translateX = scrollAnim.interpolate({
                      inputRange: [
                        -1,
                        month.startX,
                        month.startX + maxTranslate,
                        month.startX + maxTranslate + 1,
                      ],
                      outputRange: [0, 0, maxTranslate, maxTranslate],
                      extrapolate: "clamp",
                    });

                    return (
                      <View
                        key={`${month.label}-${index}`}
                        style={[styles.monthCell, { width: monthWidth }]}
                      >
                        <Animated.Text
                          numberOfLines={1}
                          style={[
                            styles.monthText,
                            { transform: [{ translateX }] },
                          ]}
                        >
                          {month.label}
                        </Animated.Text>
                      </View>
                    );
                  })}
                </View>
                <View style={styles.dayRow}>
                  {days.map(({ date, isWeekend }, index) => (
                    <View
                      key={index}
                      style={[
                        styles.dayCell,
                        isWeekend && styles.weekendCell,
                        index === todayIndex && styles.todayCell,
                      ]}
                    >
                      <Text style={styles.dayText} numberOfLines={1}>
                        {String(date.getDate()).padStart(2, "0")}
                      </Text>
                    </View>
                  ))}
                </View>
              </Animated.View>
            </View>
          </View>

          <ScrollView
            style={{ height: bodyHeight, flexGrow: 0, flexShrink: 0 }}
            nestedScrollEnabled
          >
            <View style={{ flexDirection: "row" }}>
              <View style={{ width: leftW }}>
                {rows.map((row) => (
                  <View key={row.key} style={[styles.labelCell, { height: row.height }]}>
                    <Text style={styles.labelText} numberOfLines={1}>{row.name}</Text>
                  </View>
                ))}
              </View>

              <ScrollView
                horizontal
                onScroll={onBodyScroll}
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator
              >
                <View style={{ width: gridWidth }}>
                  {rows.map((row) => (
                    <View key={row.key} style={[styles.gridRow, { height: row.height }]}>
                      {days.map(({ isWeekend }, index) => (
                        <View
                          key={index}
                          style={[
                            styles.gridCell,
                            { left: index * dayWidth },
                            isWeekend && styles.weekendCell,
                            index === todayIndex && styles.todayCell,
                          ]}
                        />
                      ))}

                      {row.bars.map(({ order, startIndex, endIndex, lane }) => {
                        const isClosed = order.state === "done" || order.state === "cancel";
                        const barTop = ROW_PAD + lane * (BAR_H + BAR_GAP);
                        return (
                          <Pressable
                            key={order.id}
                            onPress={() => {
                              const startX = startIndex * dayWidth;
                              const scrollX = scrollXRef.current;
                              const viewportWidth = availableWidth - leftW;

                              let bestLeft = startX;
                              const maxLeft = scrollX + viewportWidth - POPOVER_W;
                              if (bestLeft > maxLeft) bestLeft = maxLeft;
                              if (bestLeft < scrollX) bestLeft = scrollX;
                              bestLeft = Math.max(0, Math.min(bestLeft, gridWidth - POPOVER_W));

                              setSelected({
                                order,
                                barTop: row.top + barTop,
                                barBottom: row.top + barTop + BAR_H,
                                left: startX,
                                popoverLeft: bestLeft,
                              });
                            }}
                            style={[
                              styles.bar,
                              {
                                left: startIndex * dayWidth + 1,
                                width: (endIndex - startIndex + 1) * dayWidth - 2,
                                top: barTop,
                                backgroundColor: colorFor(
                                  order.product_name ?? String(order.id),
                                ),
                                opacity: isClosed ? 0.45 : 1,
                                borderColor:
                                  selected?.order.id === order.id
                                    ? "#1976d2"
                                    : "transparent",
                              },
                            ]}
                          >
                            <Text style={styles.barText} numberOfLines={1}>
                              {order.mo_reference} · {formatProductLabel(order)}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  ))}

                  {selected && (() => {
                    const flipAbove =
                      selected.barBottom + CARET_H + popoverHeight > contentHeight;
                    const popoverTop = flipAbove
                      ? Math.max(0, selected.barTop - CARET_H - popoverHeight)
                      : selected.barBottom + CARET_H;
                    const popoverLeft = selected.popoverLeft ?? Math.max(
                      0,
                      Math.min(selected.left, gridWidth - POPOVER_W),
                    );
                    const caretLeft = Math.max(
                      10,
                      Math.min(
                        selected.left - popoverLeft + dayWidth / 2,
                        POPOVER_W - 24,
                      ),
                    );
                    return (
                      <View
                        ref={popoverRef}
                        onLayout={(event) => setPopoverHeight(event.nativeEvent.layout.height)}
                        style={[
                          styles.popover,
                          { top: popoverTop, left: popoverLeft },
                        ]}
                      >
                        <View
                          style={[
                            flipAbove ? styles.popoverCaretBottom : styles.popoverCaret,
                            { left: caretLeft },
                          ]}
                        />
                        <View style={styles.popoverHeader}>
                          <Text style={styles.popoverTitle}>
                            {[
                              selected.order.product_name,
                              selected.order.mo_reference,
                              selected.order.name,
                            ]
                              .filter(Boolean)
                              .join(" - ")}
                          </Text>
                          <Pressable
                            style={styles.popoverClose}
                            onPress={() => setSelected(null)}
                          >
                            <Text style={styles.popoverCloseText}>✕</Text>
                          </Pressable>
                        </View>
                        <View style={styles.popoverBody}>
                          <Text style={styles.popoverRow}>
                            <Text style={styles.popoverLabel}>{t("startDate")}: </Text>
                            {formatDateTime(selected.order.date_start)}
                          </Text>
                          <Text style={styles.popoverRow}>
                            <Text style={styles.popoverLabel}>{t("stopDate")}: </Text>
                            {formatDateTime(selected.order.date_finished)}
                          </Text>
                          <Text style={styles.popoverRow}>
                            <Text style={styles.popoverLabel}>{t("workcenter")}: </Text>
                            {selected.order.workcenter_name ?? t("unassigned")}
                          </Text>
                          <Text style={styles.popoverRow}>
                            <Text style={styles.popoverLabel}>
                              {t("customerReference")}:{" "}
                            </Text>
                            {selected.order.customer_reference ?? t("unknown")}
                          </Text>
                        </View>
                      </View>
                    );
                  })()}
                </View>
              </ScrollView>
            </View>
          </ScrollView>

          <View style={styles.footerRow}>
            <View style={[styles.footerLabel, { width: leftW }]}>
              <Text style={styles.footerLabelText} numberOfLines={1}>{t("total")}</Text>
            </View>
            <View style={styles.headerClip}>
              <Animated.View
                style={{
                  width: gridWidth,
                  flexDirection: "row",
                  transform: [{ translateX: negScrollAnim }],
                }}
              >
                {dailyMinutes.map((minutes, index) => (
                  <View
                    key={index}
                    style={[
                      styles.totalCell,
                      days[index]?.isWeekend && styles.weekendCell,
                      index === todayIndex && styles.todayCell,
                    ]}
                  >
                    {minutes > 0 && (
                      <Text style={styles.totalText} numberOfLines={1}>
                        {formatMinutes(minutes)}
                      </Text>
                    )}
                  </View>
                ))}
              </Animated.View>
            </View>
          </View>
        </>
      )}

      {viewMode !== "china" && (
      <PaperProvider theme={{
        ...(isDark ? DarkTheme : DefaultTheme),
        colors: {
          ...(isDark ? DarkTheme.colors : DefaultTheme.colors),
          primary: "#1976d2",
        },
      }}>
        <DatePickerModal
          locale="en"
          mode="single"
          visible={isFromCalendarOpen}
          onDismiss={() => setIsFromCalendarOpen(false)}
          date={parseDMY(fromText) || new Date()}
          onConfirm={onConfirmFromDate}
        />
        <DatePickerModal
          locale="en"
          mode="single"
          visible={isToCalendarOpen}
          onDismiss={() => setIsToCalendarOpen(false)}
          date={parseDMY(toText) || new Date()}
          onConfirm={onConfirmToDate}
        />
      </PaperProvider>
      )}
    </View>
  );
}

const createStyles = (colors: any, isDark: boolean, dayWidth: number) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#f5f5f5",
      padding: 12,
    },
    toolbar: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: 16,
      position: "relative",
      zIndex: 50,
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 14,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: isDark ? 0.3 : 0.08,
      shadowRadius: 3,
      elevation: 2,
      borderWidth: isDark ? 1 : 0,
      borderColor: isDark ? "#333" : "transparent",
    },
    title: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    rangeGroup: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    navButton: {
      width: 28,
      alignItems: "center",
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    navButtonText: {
      fontSize: 16,
      lineHeight: 20,
      color: colors.text,
    },
    rangeFieldWrapper: {
      position: "relative",
    },
    rangeField: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    rangeFieldText: {
      fontSize: 12,
      color: colors.text,
    },
    rangeBackdrop: {
      position: "absolute",
      top: -1000,
      left: -1000,
      right: -1000,
      bottom: -1000,
      zIndex: 29,
    },
    rangeDropdown: {
      position: "absolute",
      top: "100%",
      marginTop: 4,
      minWidth: 200,
      zIndex: 30,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      paddingVertical: 6,
      shadowColor: "#000",
      shadowOpacity: 0.2,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    presetRow: {
      paddingHorizontal: 16,
      paddingVertical: 10,
    },
    presetText: {
      fontSize: 13,
      color: colors.text,
    },
    rangeDivider: {
      height: 1,
      backgroundColor: isDark ? "#333" : "#F1F5F9",
      marginVertical: 8,
    },
    customContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      paddingHorizontal: 12,
      paddingVertical: 10,
      gap: 8,
    },
    customDatesRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    customCheck: {
      fontSize: 14,
      fontWeight: "800",
      color: "#1976d2",
    },
    customLabel: {
      fontSize: 12,
      color: isDark ? "#9CA3AF" : "#64748B",
    },
    inputWrapper: {
      flexDirection: "row",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#444" : "#E2E8F0",
      borderRadius: 4,
      backgroundColor: isDark ? "#1e1e1e" : "#fff",
      width: 96,
      overflow: "hidden",
    },
    customInputIconized: {
      flex: 1,
      minWidth: 0,
      paddingHorizontal: 4,
      paddingVertical: 4,
      fontSize: 11,
      color: colors.text,
      backgroundColor: "transparent",
      ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
    },
    calendarIconBtn: {
      paddingHorizontal: 6,
      paddingVertical: 6,
      justifyContent: "center",
      alignItems: "center",
      borderLeftWidth: 1,
      borderLeftColor: isDark ? "#444" : "#E2E8F0",
      backgroundColor: isDark ? "#2A2A2A" : "#F8FAFC",
      ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
    },
    applyButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 4,
      backgroundColor: "#1976d2",
    },
    applyButtonText: {
      fontSize: 12,
      fontWeight: "700",
      color: "#fff",
    },
    chipGroup: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
    },
    chip: {
      paddingHorizontal: 14,
      paddingVertical: 6,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E2E8F0",
      backgroundColor: isDark ? "#1F2937" : "#F4F6F8",
    },
    chipActive: {
      borderColor: isDark ? "#56B7FD" : "#56B7FD",
      backgroundColor: isDark ? "#56B7FD" : "#56B7FD",
    },
    chipText: {
      fontSize: 12,
      fontWeight: "500",
      color: isDark ? "#9CA3AF" : "#475569",
      textTransform: "capitalize",
    },
    chipTextActive: {
      color: "#FFFFFF",
      fontWeight: "600",
    },
    countText: {
      fontSize: 11,
      color: colors.textMuted,
    },
    hidden: {
      display: "none",
    },
    searchContainer: {
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#444" : "#D1D5DB",
      borderRadius: 8,
      paddingHorizontal: 8,
      paddingVertical: 4,
      backgroundColor: colors.surface,
      minHeight: 36,
      width: 320,
    },
    searchChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.primary + "33" : "#DBEAFE",
      borderRadius: 20,
      paddingLeft: 10,
      paddingRight: 6,
      paddingVertical: 4,
      marginRight: 4,
      marginVertical: 2,
    },
    searchChipText: {
      fontSize: 12,
      color: isDark ? "#93C5FD" : "#1D4ED8",
      fontWeight: "600",
    },
    searchChipRemove: {
      marginLeft: 6,
      width: 16,
      height: 16,
      borderRadius: 8,
      backgroundColor: isDark ? colors.primary + "66" : "#93C5FD",
      justifyContent: "center",
      alignItems: "center",
    },
    searchChipX: {
      fontSize: 9,
      color: isDark ? "#fff" : "#1E40AF",
      fontWeight: "700",
      lineHeight: 16,
    },
    searchInputInner: {
      flex: 1,
      minWidth: 120,
      fontSize: 13,
      height: 24,
      padding: 0,
      color: colors.text,
    },
    viewToggle: {
      flexDirection: "row",
      backgroundColor: isDark ? "#1E293B" : "#F1F5F9",
      borderRadius: 8,
      padding: 4,
      gap: 2,
    },
    viewButton: {
      paddingHorizontal: 16,
      paddingVertical: 6,
      borderRadius: 6,
      backgroundColor: "transparent",
    },
    viewButtonActive: {
      backgroundColor: "#56b7fd",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    viewButtonText: {
      fontSize: 12,
      fontWeight: "500",
      color: isDark ? "#94A3B8" : "#64748B",
    },
    viewButtonTextActive: {
      color: "#fff",
      fontWeight: "600",
    },
    downloadButton: {
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: isDark ? "#1E293B" : "#F8FAFC",
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#E2E8F0",
    },
    refreshButton: {
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: isDark ? "#1E293B" : "#F8FAFC",
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#E2E8F0",
    },
    listHeaderRow: {
      flexDirection: "row",
      alignItems: "stretch",
      paddingVertical: 8,
      borderTopWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    listHeaderText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
      paddingHorizontal: 6,
    },
    listGroupRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: isDark ? "#1c1c1c" : "#fafafa",
    },
    listGroupLabel: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      paddingHorizontal: 6,
    },
    listGroupCell: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      textAlign: "right",
      paddingHorizontal: 6,
    },
    listRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderColor: colors.borderSubtle,
    },
    listRowAlt: {
      backgroundColor: isDark ? "#171717" : "#fcfcfc",
    },
    listCell: {
      fontSize: 11,
      color: colors.text,
      paddingHorizontal: 6,
    },
    listCellRight: {
      textAlign: "right",
    },
    statusChip: {
      alignSelf: "flex-start",
      marginHorizontal: 6,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 10,
      backgroundColor: isDark ? "#123" : "#e0f2f1",
    },
    statusChipText: {
      fontSize: 10,
      color: isDark ? "#8ecfcf" : "#00695c",
    },
    listTotalRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
      borderTopWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    listTotalCell: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      textAlign: "right",
      paddingHorizontal: 6,
    },
    mobileGroupHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E5E7EB",
      backgroundColor: isDark ? "#1c1c1c" : "#FFFFFF",
      gap: 8,
      shadowColor: isDark ? "#000" : "#1E293B",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
      elevation: 2,
      zIndex: 1,
    },
    mobileGroupTitle: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    mobileGroupTotals: {
      alignItems: "flex-end",
    },
    mobileGroupTotalText: {
      fontSize: 10,
      color: colors.textSecondary,
    },
    mobileCard: {
      marginTop: 8,
      marginHorizontal: 4,
      padding: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E2E8F0",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      shadowColor: isDark ? "#000" : "#1E293B",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.06,
      shadowRadius: 6,
      elevation: 2,
    },
    mobileCardHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
    },
    mobileCardTitle: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    mobileCardSubtitle: {
      fontSize: 11,
      color: colors.textSecondary,
      marginTop: 2,
    },
    mobileCardProduct: {
      fontSize: 12,
      color: colors.text,
      marginTop: 8,
    },
    mobileCardFooter: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 10,
    },
    mobileMetaChip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E2E8F0",
      backgroundColor: isDark ? "#171717" : "#F8FAFC",
      maxWidth: "100%",
    },
    mobileMetaChipText: {
      fontSize: 10,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    mobileTotalRow: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 16,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderTopWidth: 1,
      borderColor: colors.border,
    },
    mobileTotalText: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
    },
    popover: {
      position: "absolute",
      width: POPOVER_W,
      zIndex: 20,
      borderRadius: 6,
      backgroundColor: isDark ? "#2b2b2b" : "#ffffff",
      shadowColor: "#000",
      shadowOpacity: 0.25,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    popoverCaret: {
      position: "absolute",
      top: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderBottomWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderBottomColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    popoverCaretBottom: {
      position: "absolute",
      bottom: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderTopWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderTopColor: isDark ? "#2b2b2b" : "#ffffff",
    },
    popoverHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      padding: 12,
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      backgroundColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    popoverTitle: {
      flex: 1,
      fontSize: 13,
      fontWeight: "700",
      lineHeight: 18,
      color: colors.text,
      paddingRight: 8,
    },
    popoverClose: {
      paddingHorizontal: 4,
    },
    popoverCloseText: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    popoverBody: {
      padding: 12,
      gap: 4,
    },
    popoverRow: {
      fontSize: 12,
      color: colors.text,
    },
    popoverLabel: {
      fontWeight: "700",
      color: colors.text,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    loadingText: {
      marginTop: 10,
      fontSize: 12,
      color: colors.textMuted,
    },
    headerRow: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    headerClip: {
      flex: 1,
      overflow: "hidden",
    },
    cornerCell: {
      height: MONTH_H + DAY_H,
      justifyContent: "center",
      paddingLeft: 8,
      borderRightWidth: 1,
      borderColor: colors.border,
    },
    cornerText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    monthRow: {
      flexDirection: "row",
      height: MONTH_H,
    },
    monthCell: {
      height: MONTH_H,
      justifyContent: "center",
      paddingLeft: 6,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    monthText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    dayRow: {
      flexDirection: "row",
      height: DAY_H,
    },
    dayCell: {
      width: dayWidth,
      height: DAY_H,
      justifyContent: "center",
      alignItems: "center",
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
      overflow: "hidden",
    },
    dayText: {
      fontSize: 10,
      color: colors.textMuted,
    },
    weekendCell: {
      backgroundColor: isDark ? "#1b1b1b" : "#eeeeee",
    },
    todayCell: {
      backgroundColor: isDark ? "#2a2410" : "#fff8e1",
    },
    labelCell: {
      justifyContent: "center",
      paddingLeft: 8,
      borderLeftWidth: 1,
      borderBottomWidth: 1,
      borderRightWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    labelText: {
      fontSize: 12,
      color: colors.text,
    },
    gridRow: {
      position: "relative",
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    gridCell: {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: dayWidth,
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    bar: {
      position: "absolute",
      height: BAR_H,
      borderRadius: 3,
      borderWidth: 1,
      justifyContent: "center",
      paddingHorizontal: 5,
      overflow: "hidden",
    },
    barText: {
      fontSize: 10,
      color: "#2b2b2b",
    },
    footerRow: {
      flexDirection: "row",
      borderLeftWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    footerLabel: {
      height: TOTAL_H,
      justifyContent: "center",
      alignItems: "flex-end",
      paddingRight: 10,
      borderRightWidth: 1,
      borderColor: colors.border,
    },
    footerLabelText: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
    },
    totalCell: {
      width: dayWidth,
      height: TOTAL_H,
      justifyContent: "center",
      alignItems: "center",
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
      overflow: "hidden",
    },
    totalText: {
      fontSize: 9,
      color: colors.textSecondary,
    },
  });
